
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Select, 
  SelectContent, 
  SelectGroup, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, ArrowUpDown, Calendar } from "lucide-react";
import { useCountry } from "@/contexts/country";
import useCategories from "@/hooks/useCategories";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PollsFiltersProps {
  onFilterChange: (filters: PollFilters) => void;
  onSortChange?: (sort: { field: string, direction: 'asc' | 'desc' }) => void;
  activeFiltersCount?: number;
  hideCountryFilter?: boolean;
  availableCategories?: Array<{ id: string; name: string; }>;
  availableYears?: string[];
}

export interface PollFilters {
  status: string;
  country: string | null;
  constituency: string | null;
  category: string | null;
  year: string | null; // Ensure year is included in the interface
  search: string;
}

export const PollsFilters = ({ 
  onFilterChange, 
  onSortChange,
  activeFiltersCount = 0,
  hideCountryFilter = false,
  availableCategories,
  availableYears
}: PollsFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { availableCountries } = useCountry();
  const { categories, isLoading: categoriesLoading } = useCategories();
  
  // Initialize filters from URL params or defaults
  const [filters, setFilters] = useState<PollFilters>({
    status: searchParams.get('status') || 'active',
    country: searchParams.get('country') || null,
    constituency: searchParams.get('constituency') || null,
    category: searchParams.get('category') || null,
    year: searchParams.get('year') || null,
    search: searchParams.get('search') || '',
  });

  // Use provided years or generate default array from current year down to 2020
  const yearsToShow = availableYears || Array.from(
    { length: new Date().getFullYear() - 2019 },
    (_, i) => String(new Date().getFullYear() - i)
  );

  // Use provided categories or fetch all categories
  const categoriesToShow = availableCategories || categories;

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    
    if (filters.status) newParams.set('status', filters.status);
    if (filters.country) newParams.set('country', filters.country);
    if (filters.constituency) newParams.set('constituency', filters.constituency);
    if (filters.category) newParams.set('category', filters.category);
    if (filters.year) newParams.set('year', filters.year);
    if (filters.search) newParams.set('search', filters.search);
    
    setSearchParams(newParams, { replace: true });
    onFilterChange(filters);
  }, [filters, setSearchParams, onFilterChange]);

  // Handle filter changes
  const handleStatusChange = (value: string) => {
    setFilters(prev => ({ ...prev, status: value }));
  };

  const handleCountryChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      country: value === 'all' ? null : value,
      constituency: null // Reset constituency when country changes
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      category: value === 'all' ? null : value 
    }));
  };
  
  const handleYearChange = (value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      year: value === 'all' ? null : value 
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'active',
      country: hideCountryFilter ? null : null,
      constituency: null,
      category: null,
      year: null,
      search: '',
    });
  };

  const handleSortSelection = (field: string, direction: 'asc' | 'desc') => {
    if (onSortChange) {
      onSortChange({ field, direction });
    }
  };

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search polls..."
            className="pl-9"
            value={filters.search}
            onChange={handleSearchChange}
          />
        </div>
        
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            className="w-full sm:w-auto"
          >
            Clear filters
            <Badge variant="secondary" className="ml-2 bg-gray-100 text-gray-700">
              {activeFiltersCount}
            </Badge>
          </Button>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-auto">
          <Select value={filters.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Polls</SelectItem>
                <SelectItem value="active">Active Polls</SelectItem>
                <SelectItem value="closed">Closed Polls</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        {!hideCountryFilter && (
          <div className="w-full sm:w-auto">
            <Select 
              value={filters.country || 'all'} 
              onValueChange={handleCountryChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">All Countries</SelectItem>
                  {availableCountries.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div className="w-full sm:w-auto">
          <Select 
            value={filters.category || 'all'} 
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Categories</SelectItem>
                {categoriesLoading ? (
                  <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                ) : (
                  categoriesToShow.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        <div className="w-full sm:w-auto">
          <Select 
            value={filters.year || 'all'} 
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="w-full">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Years</SelectItem>
                {yearsToShow.map(year => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        <div className="w-full sm:w-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto">
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Sort by
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuLabel>Sort Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => handleSortSelection('created_at', 'desc')}>
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('created_at', 'asc')}>
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('title', 'asc')}>
                  Title (A-Z)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('title', 'desc')}>
                  Title (Z-A)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('total_votes', 'desc')}>
                  Most Responses
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('total_votes', 'asc')}>
                  Fewest Responses
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleSortSelection('closes_at', 'asc')}>
                  Closing Soon
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortSelection('closes_at', 'desc')}>
                  Closing Later
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default PollsFilters;
